/**
 * Standardized Error Handling Service
 * Provides consistent error handling patterns across the application
 */

/**
 * Custom Error Classes
 */
export class GameError extends Error {
  constructor(message, code = 'GAME_ERROR', context = {}) {
    super(message);
    this.name = 'GameError';
    this.code = code;
    this.context = context;
    this.timestamp = new Date().toISOString();
  }
}

export class ApiError extends GameError {
  constructor(message, statusCode = 500, endpoint = '', context = {}) {
    super(message, 'API_ERROR', { ...context, statusCode, endpoint });
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.endpoint = endpoint;
  }
}

export class ValidationError extends GameError {
  constructor(message, field = '', context = {}) {
    super(message, 'VALIDATION_ERROR', { ...context, field });
    this.name = 'ValidationError';
    this.field = field;
  }
}

export class NetworkError extends GameError {
  constructor(message, context = {}) {
    super(message, 'NETWORK_ERROR', context);
    this.name = 'NetworkError';
  }
}

/**
 * Error Handler Interface
 */
export class ErrorHandler {
  /**
   * Handle error
   * @param {Error} _error - Error to handle
   * @param {Object} _context - Additional context
   */
  // eslint-disable-next-line no-unused-vars
  handle(_error, _context = {}) {
    throw new Error('Must implement handle method');
  }
}

/**
 * Console Error Handler
 */
export class ConsoleErrorHandler extends ErrorHandler {
  handle(error, context = {}) {
    const errorInfo = {
      name: error.name,
      message: error.message,
      code: error.code,
      timestamp: error.timestamp || new Date().toISOString(),
      context: { ...error.context, ...context },
      stack: error.stack
    };

    console.group(`🚨 ${error.name || 'Error'}`);
    console.error('Message:', error.message);
    console.error('Code:', error.code || 'UNKNOWN');
    console.error('Context:', errorInfo.context);
    if (error.stack) {
      console.error('Stack:', error.stack);
    }
    console.groupEnd();
  }
}

/**
 * Notification Error Handler
 */
export class NotificationErrorHandler extends ErrorHandler {
  constructor(notificationService) {
    super();
    this.notificationService = notificationService;
  }

  // eslint-disable-next-line no-unused-vars
  handle(error, _context = {}) {
    const message = this.getUserFriendlyMessage(error);
    const type = this.getNotificationType(error);

    if (this.notificationService) {
      this.notificationService.show(message, type);
    } else if (window.$buefy) {
      window.$buefy.notification.open({
        message,
        type: type === 'error' ? 'is-danger' : 'is-warning',
        position: 'is-bottom-right',
        duration: 5000
      });
    } else {
      // Fallback to alert
      alert(message);
    }
  }

  getUserFriendlyMessage(error) {
    const friendlyMessages = {
      'API_ERROR': '伺服器連線發生錯誤，請稍後再試',
      'NETWORK_ERROR': '網路連線不穩定，請檢查網路狀態',
      'VALIDATION_ERROR': '輸入資料有誤，請檢查後重試',
      'GAME_ERROR': '遊戲發生錯誤，請重新整理頁面'
    };

    return friendlyMessages[error.code] || error.message || '發生未知錯誤';
  }

  getNotificationType(error) {
    const typeMapping = {
      'API_ERROR': 'error',
      'NETWORK_ERROR': 'error',
      'VALIDATION_ERROR': 'warning',
      'GAME_ERROR': 'error'
    };

    return typeMapping[error.code] || 'error';
  }
}

/**
 * Composite Error Handler
 */
export class CompositeErrorHandler extends ErrorHandler {
  constructor(handlers = []) {
    super();
    this.handlers = handlers;
  }

  addHandler(handler) {
    if (!(handler instanceof ErrorHandler)) {
      throw new Error('Handler must be an instance of ErrorHandler');
    }
    this.handlers.push(handler);
  }

  handle(error, context = {}) {
    this.handlers.forEach(handler => {
      try {
        handler.handle(error, context);
      } catch (handlerError) {
        console.error('Error in error handler:', handlerError);
      }
    });
  }
}

/**
 * Error Handler Factory
 */
export class ErrorHandlerFactory {
  static createConsoleHandler() {
    return new ConsoleErrorHandler();
  }

  static createNotificationHandler(notificationService = null) {
    return new NotificationErrorHandler(notificationService);
  }

  static createCompositeHandler(handlers = []) {
    return new CompositeErrorHandler(handlers);
  }

  static createDefault(notificationService = null) {
    return new CompositeErrorHandler([
      new ConsoleErrorHandler(),
      new NotificationErrorHandler(notificationService)
    ]);
  }
}

/**
 * Error Service
 */
export class ErrorService {
  constructor(errorHandler = null) {
    this.errorHandler = errorHandler || ErrorHandlerFactory.createDefault();
  }

  /**
   * Handle error with context
   * @param {Error} error - Error to handle
   * @param {Object} context - Additional context
   */
  handleError(error, context = {}) {
    this.errorHandler.handle(error, context);
  }

  /**
   * Create and handle API error
   * @param {string} message - Error message
   * @param {number} statusCode - HTTP status code
   * @param {string} endpoint - API endpoint
   * @param {Object} context - Additional context
   */
  handleApiError(message, statusCode, endpoint, context = {}) {
    const error = new ApiError(message, statusCode, endpoint, context);
    this.handleError(error, context);
  }

  /**
   * Create and handle validation error
   * @param {string} message - Error message
   * @param {string} field - Field name
   * @param {Object} context - Additional context
   */
  handleValidationError(message, field, context = {}) {
    const error = new ValidationError(message, field, context);
    this.handleError(error, context);
  }

  /**
   * Wrap async function with error handling
   * @param {Function} asyncFn - Async function to wrap
   * @param {Object} context - Error context
   * @returns {Function} Wrapped function
   */
  wrapAsync(asyncFn, context = {}) {
    return async (...args) => {
      try {
        return await asyncFn(...args);
      } catch (error) {
        this.handleError(error, context);
        throw error;
      }
    };
  }

  /**
   * Set error handler
   * @param {ErrorHandler} errorHandler - New error handler
   */
  setErrorHandler(errorHandler) {
    if (!(errorHandler instanceof ErrorHandler)) {
      throw new Error('Error handler must be an instance of ErrorHandler');
    }
    this.errorHandler = errorHandler;
  }
}

// Default error service instance
export const errorService = new ErrorService();

/**
 * Utility function to handle async operations with error handling
 * @param {Promise} promise - Promise to handle
 * @param {Object} context - Error context
 * @returns {Promise<[Error|null, any]>} [error, result] tuple
 */
export const handleAsync = async (promise, context = {}) => {
  try {
    const result = await promise;
    return [null, result];
  } catch (error) {
    errorService.handleError(error, context);
    return [error, null];
  }
};